"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HighlightedText,
  type HighlightTone,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  DataTable,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  CheckerInput,
  ToneLegend,
} from "@/components/tools/style-checkers-a/a-frame";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type AbbreviationMatch,
  checkLatinAbbreviations,
  englishFor,
  type IssueKind,
  type StyleChoice,
} from "@/lib/tool-kit/style-checkers/latin-abbreviations";

/** English strings. A translated content file overrides these through `ui`. */
export const LATIN_ABBREVIATION_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, an essay section or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "Many reference managers, e.g., Zotero and Mendeley, are free. Some tools (e.g. Zotero) also import PDFs. The sample was small, i.e., 40 students, so we pooled the groups (cf. Smith et al, 2020). We tested apples, pears, and etc. Treated vs. untreated groups were compared (vs control). Smith et. al. found no effect.",
  styleLabel: "Check against",
  styleApa: "APA 7",
  styleChicago: "Chicago 18",
  stylePunctuation: "Punctuation only",
  ignoreQuotes: "Ignore quoted text",
  enterPrompt:
    "Paste or type some text to check its Latin abbreviations — e.g., i.e., etc., cf., viz., vs. and et al.",
  results: "Results",
  found: "Abbreviations found",
  flagged: "With an issue",
  outside: "Outside parentheses",
  words: "Words",
  sentences: "Sentences",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with Latin abbreviations highlighted",
  legendFine: "Fine as written",
  legendPlacement: "Use the English here",
  legendAdvice: "Consider the English",
  legendPunctuation: "Punctuation",
  legendRedundant: "Redundant",
  hoverHint:
    "Hover over a highlight to see the issue and the replacement; the table below lists them all.",
  noneFound: "No Latin abbreviations found.",
  allFine:
    "Every abbreviation is punctuated and placed correctly for this style.",
  findingsHeading: "What to change",
  colWritten: "As written",
  colIssue: "Issue",
  colReplace: "Replace with",
  colSentence: "In your sentence",
  usedHeading: "Abbreviations used",
  colAbbreviation: "Abbreviation",
  colTimes: "Times",
  colInside: "In parentheses",
  colOutside: "Outside",
  issuePeriods:
    "Missing or misplaced periods: the standard form is “{standard}”.",
  issueComma: "“{abbreviation}” takes a comma after it, as “{english},” would.",
  issueDoublePeriod:
    "Two periods in a row: the abbreviation's own period already ends the sentence.",
  issuePlacementApa:
    "APA 7 keeps this abbreviation inside parentheses; in running text write “{english}”.",
  issuePlacementChicago:
    "Chicago usually keeps this abbreviation for parentheses and notes; in running text consider “{english}”.",
  issueRedundantAnd: "“and etc.” says “and” twice; drop the “and”.",
  issueRedundantList:
    "A list introduced by “such as”, “for example” or “e.g.” already reads as partial; “etc.” repeats that.",
  issueListComma: "Put a comma before “etc.” when it ends a list.",
  noReplacement: "Keep as written",
  judgement:
    "Rules, not taste: APA 7 §6.29 keeps these abbreviations inside parentheses. Quotations, titles and reference entries keep the original wording, so check those by hand.",
} as const;

type Strings = typeof LATIN_ABBREVIATION_CHECKER_STRINGS;

type Category = "fine" | "placement" | "advice" | "punctuation" | "redundant";

const TONES: Record<Category, HighlightTone> = {
  fine: "emerald",
  placement: "rose",
  advice: "amber",
  punctuation: "sky",
  redundant: "violet",
};

function categoryOf(match: AbbreviationMatch): Category {
  const placement = match.issues.find((issue) => issue.kind === "placement");
  if (placement) {
    return placement.firm ? "placement" : "advice";
  }
  if (match.issues.some((issue) => issue.kind === "redundant")) {
    return "redundant";
  }
  return match.issues.length > 0 ? "punctuation" : "fine";
}

function issueText(
  kind: IssueKind,
  firm: boolean,
  match: AbbreviationMatch,
  style: StyleChoice,
  strings: Strings
): string {
  const english = englishFor(match.key, style);
  const abbreviation = match.key;
  switch (kind) {
    case "periods":
      return t(strings.issuePeriods, { standard: abbreviation });
    case "comma":
      return t(strings.issueComma, { abbreviation, english });
    case "double-period":
      return strings.issueDoublePeriod;
    case "placement":
      return t(
        style === "apa"
          ? strings.issuePlacementApa
          : strings.issuePlacementChicago,
        { english }
      );
    case "redundant":
      return firm ? strings.issueRedundantAnd : strings.issueRedundantList;
    default:
      return strings.issueListComma;
  }
}

function issuesOf(
  match: AbbreviationMatch,
  style: StyleChoice,
  strings: Strings
): string {
  return match.issues
    .map((issue) => issueText(issue.kind, issue.firm, match, style, strings))
    .join(" ");
}

export default function LatinAbbreviationCheckerWidget() {
  const strings = useToolUi(LATIN_ABBREVIATION_CHECKER_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [style, setStyle] = useState<StyleChoice>("apa");
  const [ignoreQuotes, setIgnoreQuotes] = useState(true);
  const deferred = useDeferredValue(text);
  const report = useMemo(
    () => checkLatinAbbreviations(deferred, { style, ignoreQuotes }),
    [deferred, style, ignoreQuotes]
  );
  const flagged = report.matches.filter((match) => match.issues.length > 0);
  const outside = report.rows
    .filter((row) => row.key !== "et al.")
    .reduce((sum, row) => sum + row.outside, 0);
  const highlights: TextMatch[] = report.matches.map((match) => ({
    start: match.start,
    end: match.end,
    category: categoryOf(match),
    title:
      match.issues.length > 0
        ? issuesOf(match, style, strings)
        : strings.legendFine,
  }));
  const hasText = deferred.trim().length > 0;

  return (
    <div className="space-y-6">
      <CheckerInput
        clearLabel={strings.clear}
        example={strings.exampleText}
        exampleLabel={strings.tryExample}
        extra={
          <Checkbox
            checked={ignoreQuotes}
            label={strings.ignoreQuotes}
            onChange={setIgnoreQuotes}
          />
        }
        hint={strings.privacy}
        id="lac-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        value={text}
      />
      <PillGroup
        label={strings.styleLabel}
        name="lac-style"
        onChange={setStyle}
        options={[
          { value: "apa" as StyleChoice, label: strings.styleApa },
          { value: "chicago" as StyleChoice, label: strings.styleChicago },
          {
            value: "punctuation" as StyleChoice,
            label: strings.stylePunctuation,
          },
        ]}
        value={style}
      />

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.found}
                value={fmt(report.matches.length)}
              />
              <StatCard label={strings.flagged} value={fmt(report.flagged)} />
              <StatCard label={strings.outside} value={fmt(outside)} />
              <StatCard label={strings.words} value={fmt(report.words)} />
              <StatCard
                label={strings.sentences}
                value={fmt(report.sentences)}
              />
            </StatGrid>
            <p className={HINT}>{strings.judgement}</p>
          </div>

          {report.matches.length === 0 ? (
            <Notice>{strings.noneFound}</Notice>
          ) : (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
              <ToneLegend
                items={[
                  { tone: TONES.placement, label: strings.legendPlacement },
                  { tone: TONES.advice, label: strings.legendAdvice },
                  { tone: TONES.punctuation, label: strings.legendPunctuation },
                  { tone: TONES.redundant, label: strings.legendRedundant },
                  { tone: TONES.fine, label: strings.legendFine },
                ]}
              />
              <HighlightedText
                label={strings.highlightLabel}
                matches={highlights}
                text={deferred}
                tones={TONES}
              />
              <p className={HINT}>{strings.hoverHint}</p>
            </div>
          )}

          {report.matches.length > 0 && flagged.length === 0 ? (
            <Notice>{strings.allFine}</Notice>
          ) : null}

          {flagged.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.findingsHeading}</h3>
              <DataTable
                head={[
                  strings.colWritten,
                  strings.colIssue,
                  strings.colReplace,
                  strings.colSentence,
                ]}
                rows={flagged.map((match) => [
                  match.text,
                  issuesOf(match, style, strings),
                  match.suggestion ?? strings.noReplacement,
                  match.sentence,
                ])}
              />
            </div>
          ) : null}

          {report.rows.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.usedHeading}</h3>
              <DataTable
                head={[
                  strings.colAbbreviation,
                  strings.colTimes,
                  strings.colInside,
                  strings.colOutside,
                ]}
                rows={report.rows.map((row) => [
                  row.key,
                  fmt(row.count),
                  fmt(row.inside),
                  fmt(row.outside),
                ])}
              />
            </div>
          ) : null}
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
